import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Users, CheckCircle2, Clock, DollarSign, Mail, Settings, Save, Loader2, KeyRound, Copy, Check } from "lucide-react";
import ChangePasswordCard from "../components/ChangePasswordCard";
import TwoFactorCard from "../components/TwoFactorCard";
import NotificationPreferencesCard from "../components/NotificationPreferencesCard";

const STATUSES = ["pending","confirmed","in_progress","completed","cancelled"];

const SETTING_FIELDS = [
  { key: "google_review_url", label: "Google Review URL", placeholder: "https://g.page/r/..." },
  { key: "contact_email", label: "Contact Email", placeholder: "info@shonicskleaningsolutions.com" },
  { key: "contact_phone", label: "Contact Phone", placeholder: "943-245-0462" },
  { key: "promo_text", label: "Promo Strip Text", placeholder: "First-time customers save 10%..." },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({ google_review_url: "", contact_email: "", contact_phone: "", promo_text: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [pendingResets, setPendingResets] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = async () => {
    try {
      const [a, b, l, s, pr] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/bookings"),
        api.get("/leads"),
        api.get("/settings"),
        api.get("/admin/password-resets"),
      ]);
      setData(a.data);
      setBookings(b.data);
      setLeads(l.data);
      setSettings(s.data);
      setPendingResets(pr.data || []);
    } catch { toast.error("Could not load admin data"); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Could not update"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Could not save settings"); }
    setSavingSettings(false);
  };

  if (!data) return <div className="container-x py-24 text-[#0B5D3B]">Loading…</div>;

  const stats = data.stats;

  return (
    <div className="container-x py-16 md:py-24" data-testid="admin-dashboard">
      <div className="overline mb-2">Operations Console</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#111] mb-12">ShoNics Command Center</h1>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="admin-kpis">
        {[
          { label: "Revenue", value: `$${stats.total_revenue}`, icon: DollarSign, color: "#0B5D3B" },
          { label: "Bookings", value: stats.total_bookings, icon: TrendingUp, color: "#D4AF37" },
          { label: "Completed", value: stats.completed_jobs, icon: CheckCircle2, color: "#0B5D3B" },
          { label: "Pending", value: stats.pending_jobs, icon: Clock, color: "#D4AF37" },
          { label: "Clients", value: stats.total_clients, icon: Users, color: "#0B5D3B" },
          { label: "New leads", value: stats.total_leads, icon: Mail, color: "#D4AF37" },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="card-luxe p-5" data-testid={`kpi-${i}`}>
              <Icon className="w-4 h-4 mb-3" style={{ color: k.color }} />
              <div className="font-serif text-2xl text-[#111]">{k.value}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A8A81] mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div className="card-luxe p-6 lg:col-span-2" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Revenue trend</h3>
            <span className="text-xs text-[#7A8A81]">Last 6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D2" />
              <XAxis dataKey="month" stroke="#7A8A81" fontSize={12} />
              <YAxis stroke="#7A8A81" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE3D2" }} />
              <Line type="monotone" dataKey="revenue" stroke="#0B5D3B" strokeWidth={3} dot={{ fill: "#D4AF37", r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-luxe p-6" data-testid="services-chart">
          <h3 className="font-serif text-xl mb-4">Bookings by service</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.service_series} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D2" />
              <XAxis type="number" stroke="#7A8A81" fontSize={12} />
              <YAxis dataKey="service" type="category" stroke="#7A8A81" fontSize={11} width={100} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE3D2" }} />
              <Bar dataKey="count" fill="#D4AF37" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Site Settings */}
      <div className="card-luxe p-6 mb-6" data-testid="site-settings-card">
        <div className="flex items-center gap-2 mb-5">
          <Settings className="w-5 h-5 text-[#0B5D3B]" />
          <h3 className="font-serif text-xl">Site Settings</h3>
          <span className="ml-auto text-xs text-[#7A8A81]">Updates apply instantly across the site</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SETTING_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7A8A81] mb-1.5 block">{f.label}</label>
              <input
                value={settings[f.key] || ""}
                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
                data-testid={`setting-${f.key}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="btn-primary text-sm py-2.5 px-5 disabled:opacity-60"
            data-testid="save-settings-btn"
          >
            {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save settings</>}
          </button>
          {settings.google_review_url && (
            <a
              href={settings.google_review_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#0B5D3B] underline hover:text-[#063A24]"
            >
              Test review link →
            </a>
          )}
        </div>
      </div>

      {/* Bookings table */}
      <div className="card-luxe p-6 mb-6" data-testid="bookings-table">
        <h3 className="font-serif text-xl mb-4">All bookings</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#7A8A81] border-b border-[#EAE3D2]">
                <th className="py-3 pr-4">Client</th>
                <th className="py-3 pr-4">Service</th>
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">City</th>
                <th className="py-3 pr-4">Total</th>
                <th className="py-3 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-[#EAE3D2] hover:bg-[#FAF7EE]" data-testid={`row-${b.id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-medium text-[#111]">{b.user_name}</div>
                    <div className="text-xs text-[#7A8A81]">{b.user_email}</div>
                  </td>
                  <td className="py-3 pr-4">{b.service_name}</td>
                  <td className="py-3 pr-4">{b.date}<br /><span className="text-xs text-[#7A8A81]">{b.time_slot}</span></td>
                  <td className="py-3 pr-4">{b.city}</td>
                  <td className="py-3 pr-4 font-semibold text-[#0B5D3B]">${b.estimated_price}</td>
                  <td className="py-3 pr-4">
                    <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="px-3 py-1.5 rounded-full border border-[#EAE3D2] text-xs"
                      data-testid={`status-select-${b.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-[#7A8A81]">No bookings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leads */}
      <div className="card-luxe p-6 mb-6" data-testid="leads-table">
        <h3 className="font-serif text-xl mb-4">Recent leads</h3>
        {leads.length === 0 ? <div className="text-[#7A8A81] text-sm">No leads yet.</div> : (
          <div className="space-y-3">
            {leads.slice(0,8).map((l) => (
              <div key={l.id} className="p-4 rounded-xl border border-[#EAE3D2] flex flex-wrap gap-3 justify-between items-start">
                <div>
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-[#7A8A81]">{l.email} · {l.phone}</div>
                  <div className="text-sm mt-1 text-[#4A554E]">{l.message}</div>
                </div>
                <div className="text-xs text-[#7A8A81]">{new Date(l.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ChangePasswordCard />
      <div className="mt-6">
        <TwoFactorCard />
      </div>
      <div className="mt-6">
        <NotificationPreferencesCard />
      </div>

      {/* Pending password resets */}
      <div className="card-luxe p-6 mt-6" data-testid="pending-resets-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-[#0B5D3B]" />
          <h3 className="font-serif text-xl">Pending password resets</h3>
          <span className="ml-auto text-xs text-[#7A8A81]">Copy and send manually until SendGrid is configured</span>
        </div>
        {pendingResets.length === 0 ? (
          <div className="text-[#7A8A81] text-sm">No pending resets.</div>
        ) : (
          <div className="space-y-3">
            {pendingResets.map((r) => {
              const url = `${window.location.origin}/reset-password?token=${r.token}`;
              return (
                <div key={r.id} className="p-4 rounded-xl border border-[#EAE3D2] flex flex-wrap items-center gap-3" data-testid={`reset-${r.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-sm">{r.user_name || r.email}</div>
                    <div className="text-xs text-[#7A8A81]">{r.email}</div>
                  </div>
                  <code className="text-[10px] bg-[#FAF7EE] px-2 py-1 rounded font-mono max-w-[280px] truncate">{url}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(url);
                      setCopiedToken(r.id);
                      setTimeout(() => setCopiedToken(null), 1800);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#0B5D3B] text-white hover:bg-[#063A24] inline-flex items-center gap-1.5"
                    data-testid={`copy-reset-${r.id}`}
                  >
                    {copiedToken === r.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy link</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
